import React from 'react';
import { Button as MuiButton } from '@mui/material';
import { motion } from 'framer-motion';
import { styled } from '@mui/material/styles';

/**
 * Pulse Button Component with continuous pulse animation
 * Perfect for CTAs and important actions
 */
const PulseButtonWrapper = styled(motion.div)({
  display: 'inline-block',
  position: 'relative',
});

const StyledPulseButton = styled(MuiButton)(({ theme }) => ({
  borderRadius: theme.custom.borderRadius.md,
  textTransform: 'none',
  fontWeight: 700,
  padding: '12px 32px',
  fontSize: '1.125rem',
  boxShadow: theme.custom.boxShadow.md,
  position: 'relative',
  overflow: 'hidden',
  
  '&:hover': {
    boxShadow: theme.custom.boxShadow.lg,
    transform: 'translateY(-2px)',
  },
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '100%',
    height: '100%',
    background: 'rgba(255, 255, 255, 0.3)',
    borderRadius: '50%',
    transform: 'translate(-50%, -50%) scale(0)',
    transition: 'transform 0.5s ease-out',
  },
  
  '&:hover::before': {
    transform: 'translate(-50%, -50%) scale(2.5)',
    opacity: 0,
  },
}));

const PulseButton = ({ 
  children, 
  variant = 'contained',
  color = 'primary',
  size = 'large',
  fullWidth = false,
  disabled = false,
  pulsing = true,
  startIcon,
  endIcon,
  onClick,
  ...props 
}) => {
  const pulseAnimation = {
    scale: [1, 1.05, 1],
    boxShadow: [
      '0 4px 6px rgba(0, 0, 0, 0.1)',
      '0 8px 16px rgba(0, 0, 0, 0.2)',
      '0 4px 6px rgba(0, 0, 0, 0.1)',
    ],
  };

  const motionProps = pulsing && !disabled ? {
    animate: pulseAnimation,
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  } : {};

  return (
    <PulseButtonWrapper {...motionProps}>
      <StyledPulseButton
        variant={variant}
        color={color}
        size={size}
        fullWidth={fullWidth}
        disabled={disabled}
        startIcon={startIcon}
        endIcon={endIcon}
        onClick={onClick}
        {...props}
      >
        {children}
      </StyledPulseButton>
    </PulseButtonWrapper>
  );
};

export default PulseButton;
